import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useIFC } from '../../hooks/useIFC';
import { MapService } from '../../services/map/mapService';
import IFCService from '../../services/ifc/ifcService';
import { useGlobalStore, useUIStore } from '../../stores';
import { useIFCFineTuningStore } from '../../stores/ifcFineTuningStore';
import { logger } from '../../../../shared/utils/logger';
import './IfcInputPanel.css';

interface IfcInputPanelProps {
  isOpen: boolean;
  onClose: () => void;
  mapService: MapService | null;
}

export const IfcInputPanel: React.FC<IfcInputPanelProps> = ({ isOpen, onClose, mapService }) => {
  const {
    loadIFCModel,
    changeStorey,
    toggleCategory,
    getCategoryName,
    currentStorey,
    availableStoreys,
    availableCategories,
    selectedCategories,
    currentStoreyId,
    debug,
    currentModel
  } = useIFC(mapService);
  const { selectedStoreyId, setSelectedStoreyId, elevationMeters, elevationType } = useGlobalStore();
  const { addNotification } = useUIStore();
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [rotation, setRotation] = useState(0);
  const [offsetXInput, setOffsetXInput] = useState('0');
  const [offsetYInput, setOffsetYInput] = useState('0');
  const [rotationInput, setRotationInput] = useState('0');
  const elevation = elevationMeters;

  const ifcService = IFCService.getInstance();

  // Función helper para actualizar el modelo en tiempo real
  const updateModelInRealTime = useCallback((newOffsetX: number, newOffsetY: number, newRotation: number) => {
    if (!ifcService.hasLoadedModel()) return;
    
    const ifcModel = ifcService.getCurrentIfcModel();
    if (ifcModel) {
      ifcModel.updateFineTuning(newOffsetX, newOffsetY, newRotation);
    }
  }, [ifcService]);

  const currentFingerprint = currentModel?.fingerprint ?? null;
  const currentModelLabel = currentModel?.name || currentModel?.file?.name || 'Modelo IFC';
  const formatMeters = (value: number) => Number.isFinite(value) ? value.toFixed(2) : '0.00';
  const formatRotation = (value: number) => Number.isFinite(value) ? value.toFixed(1) : '0.0';

  const resetLocalFineTuning = useCallback(() => {
    setOffsetX(0);
    setOffsetY(0);
    setRotation(0);
    setOffsetXInput('0');
    setOffsetYInput('0');
    setRotationInput('0');
  }, []);

  useEffect(() => {
    if (!currentFingerprint) {
      resetLocalFineTuning();
      return;
    }

    const fineTuningState = useIFCFineTuningStore.getState();
    const storedFingerprint = fineTuningState.modelFingerprint;

    if (!storedFingerprint || storedFingerprint !== currentFingerprint) {
      resetLocalFineTuning();
      updateModelInRealTime(0, 0, 0);
      return;
    }

    const { offsetX: persistedOffsetX, offsetY: persistedOffsetY, rotation: persistedRotation } = fineTuningState.offsets;

    setOffsetX(persistedOffsetX);
    setOffsetY(persistedOffsetY);
    setRotation(persistedRotation);
    setOffsetXInput(formatMeters(persistedOffsetX));
    setOffsetYInput(formatMeters(persistedOffsetY));
    setRotationInput(formatRotation(persistedRotation));
    updateModelInRealTime(persistedOffsetX, persistedOffsetY, persistedRotation);
  }, [currentFingerprint, resetLocalFineTuning, updateModelInRealTime]);

  // Efecto para monitorear cambios en las categorías
  useEffect(() => {
    logger.log('IFC_INPUT_PANEL', '🔄 IfcInputPanel: Categorías actualizadas:', {
      availableCategories,
      selectedCategories,
      currentStoreyId,
      hasCurrentModel: !!currentModel
    });
  }, [availableCategories, selectedCategories, currentStoreyId, currentModel]);

  // Sincronización: actualizar la planta del IFC cuando el store global cambie desde otros paneles
  useEffect(() => {
    // Solo sincronizar si hay un cambio real y no es el render inicial
    if (selectedStoreyId && 
        selectedStoreyId !== currentStoreyId && 
        availableStoreys.length > 0 && 
        currentModel &&
        currentStoreyId) { // Asegurar que ya hay una planta actual
      logger.log('IFC_INPUT_PANEL', '🔄 IfcInputPanel: Sincronizando con store global:', selectedStoreyId);
      const storeyExists = availableStoreys.some((storey: any) => 
        String(storey.GlobalId?.value) === selectedStoreyId || String(storey.expressID) === selectedStoreyId
      );
      if (storeyExists) {
        changeStorey(selectedStoreyId);
      }
    }
  }, [selectedStoreyId, currentStoreyId, availableStoreys, currentModel]);

  // Efecto para monitorear cambios en la elevación
  useEffect(() => {
    logger.log('IFC_INPUT_PANEL', '📏 IfcInputPanel: Elevación actualizada:', {
      elevationMeters,
      elevationType,
      elevation
    });
  }, [elevationMeters, elevationType, elevation]);

  const lastSyncedElevationRef = useRef<{ elevation: number; type: 'ortometric' | 'ellipsoidal' } | null>(null);

  useEffect(() => {
    if (!ifcService.hasLoadedModel()) return;

    const safeElevation = Number.isFinite(elevation) ? elevation : 0;
    const lastSynced = lastSyncedElevationRef.current;
    if (lastSynced && lastSynced.elevation === safeElevation && lastSynced.type === elevationType) {
      return;
    }

    let cancelled = false;

    const syncElevation = async () => {
      const ok = await ifcService.setModelElevation(safeElevation, elevationType);
      if (ok && !cancelled) {
        lastSyncedElevationRef.current = { elevation: safeElevation, type: elevationType };
        logger.log('IFC_INPUT_PANEL', '✅ Elevación sincronizada automáticamente con IFCService');
      }
    };

    syncElevation();

    return () => {
      cancelled = true;
    };
  }, [elevation, elevationType, ifcService]);

  if (!isOpen) return null;

  // Debug logging
      logger.log('IFC_INPUT_PANEL', '🔍 IfcInputPanel Debug:', {
    availableCategories,
    selectedCategories,
    currentStorey,
    currentStoreyId,
    debug
  });

  // Fallback: si no hay categorías filtradas, usar todas las categorías del modelo
  const categoriesToShow = availableCategories.length > 0 
    ? availableCategories 
    : (debug?.hasCurrentModel ? currentModel?.categories || [] : []);

      logger.log('IFC_INPUT_PANEL', '🎨 Categorías a mostrar:', categoriesToShow);

  const handleLoadIFC = async () => {
    // Crear un input file temporal
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.ifc';
    
    input.onchange = async (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) {
        console.error('❌ No se seleccionó ningún archivo');
        return;
      }
      
      // Usar el hook para cargar el modelo
      await loadIFCModel(file);
    };
    
    // Abrir el selector de archivos
    input.click();
  };

  const handleStoreyChange = (storeyId: string) => {
    logger.log('IFC_INPUT_PANEL', '🔄 IfcInputPanel: Cambiando planta a:', storeyId);
    // Actualizar store global y lógica IFC (categorías, etc.)
    setSelectedStoreyId(storeyId);
    changeStorey(storeyId);
    // Cambiar la planta en el mapa como hace ElementsTablePanel
    const gui = mapService?.getGUI();
    if (gui) {
      gui.selectStoreyById(storeyId);
      logger.log('IFC_INPUT_PANEL', '✅ IfcInputPanel: Planta cambiada en el mapa a:', storeyId);
    } else {
      logger.warn('IFC_INPUT_PANEL', '⚠️ IfcInputPanel: GUI no disponible para cambiar planta');
    }
  };

  const handleCenterOnMap = () => {
    try {
      const ifcModel = ifcService.getCurrentIfcModel();
      if (ifcModel) {
        const success = ifcModel.centerOnMap();
        if (success) {
          logger.log('IFC_INPUT_PANEL', '🎯 IFC centrado en el mapa');
        } else {
          console.warn('⚠️ No se pudo centrar el IFC en el mapa');
        }
      } else {
        console.warn('⚠️ No hay modelo IFC cargado');
      }
    } catch (error) {
      console.error('❌ Error al centrar IFC:', error);
    }
  };

  const handleApplyFineTuning = () => {
    try {
      const ifcModel = ifcService.getCurrentIfcModel();
      if (ifcModel) {
        logger.log('IFC_INPUT_PANEL', '💾 Guardando ajustes finos permanentemente:', { offsetX, offsetY, rotation });
        if (!currentFingerprint) {
          addNotification({ 
            type: 'warning', 
            title: 'Modelo no identificado', 
            message: 'No se pudieron guardar los ajustes porque el modelo IFC no tiene identificador' 
          });
          return;
        }

        useIFCFineTuningStore.getState().saveAdjustments(
          currentFingerprint,
          { offsetX, offsetY, rotation },
          currentModelLabel
        );

        addNotification({ 
          type: 'success', 
          title: 'Ajustes guardados', 
            message: `Posición guardada: X=${offsetX.toFixed(2)}m, Y=${offsetY.toFixed(2)}m, Rotación=${rotation.toFixed(1)}°. Se restaurará al volver a abrir este IFC.` 
        });
      } else {
        console.warn('⚠️ No hay modelo IFC cargado para aplicar ajustes');
      }
    } catch (error) {
      console.error('❌ Error al aplicar ajustes finos:', error);
    }
  };

  // Elevación automática del IFC se calcula ahora durante la carga; se elimina la UI de ajustes manuales.



  const handleResetFineTuning = () => {
    resetLocalFineTuning();
    
    try {
      const ifcModel = ifcService.getCurrentIfcModel();
      if (ifcModel) {
        logger.log('IFC_INPUT_PANEL', '🔄 Reseteando ajustes finos');
        ifcModel.updateFineTuning(0, 0, 0);
        useIFCFineTuningStore.getState().clearAdjustments();
      }
    } catch (error) {
      console.error('❌ Error al resetear ajustes finos:', error);
    }
  };

  return (
    <div className="v2-ifc-input-panel">
      <div className="v2-ifc-input-panel-header">
        <h3 className="v2-ifc-input-panel-title">Cargar Modelo IFC</h3>
        <button 
          className="v2-ifc-input-panel-close"
          onClick={onClose}
        >
          ×
        </button>
      </div>
      
      <div className="v2-ifc-input-panel-content">
        <div className="v2-ifc-input-panel-section">
          <h4>Cargar Archivo</h4>
          <button 
            className="v2-ifc-input-panel-button"
            onClick={handleLoadIFC}
          >
            Cargar IFC
          </button>
        </div>

        {availableStoreys.length > 0 && (
          <div className="v2-ifc-input-panel-section">
            <h4>Seleccionar Planta</h4>
            <select 
              className="v2-ifc-input-panel-select"
              value={selectedStoreyId}
              onChange={(e) => {
                logger.log('IFC_INPUT_PANEL', '🔄 IfcInputPanel: Cambiando planta a:', e.target.value);
                handleStoreyChange(e.target.value);
              }}
            >
              {(() => {
                const gui = mapService?.getGUI();
                const storeyOptions = gui?.getStoreyDropdownOptions() || [];
                if (storeyOptions.length === 0) {
                  return <option value="">Cargar IFC primero</option>;
                }
                return storeyOptions.map((opt: any) => (
                  <option key={opt.id} value={opt.id}>{opt.name}</option>
                ));
              })()}
            </select>
            <button 
              className="v2-ifc-input-panel-button"
              onClick={handleCenterOnMap}
              style={{ marginTop: '12px' }}
            >
              Centrar en mapa
            </button>
          </div>
        )}

        {categoriesToShow.length > 0 && (
          <div className="v2-ifc-input-panel-section">
            <h4>Gestionar Categorías</h4>
            <div className="v2-ifc-input-panel-categories">
              {categoriesToShow.map((category: string) => (
                <button
                  key={category}
                  className={`v2-ifc-input-panel-category-button ${
                    selectedCategories.includes(category) ? 'active' : ''
                  }`}
                  onClick={() => toggleCategory(category)}
                >
                  {getCategoryName(category)}
                </button>
              ))}
            </div>
            
          </div>
        )}

        <div className="v2-ifc-input-panel-section">
          <h4>Ajustes Finos</h4>
          <div className="v2-ifc-input-panel-row">
            <div>
              <label className="v2-ifc-input-panel-label">Este/X (m)</label>
                             <input
                 className="v2-ifc-input-panel-input"
                 type="number"
                 step="0.01"
                 value={offsetXInput}
                 onChange={(e) => {
                   const value = e.target.value;
                   setOffsetXInput(value);
                   if (value === '' || value === '-') {
                     setOffsetX(0);
                     updateModelInRealTime(0, offsetY, rotation);
                   } else {
                     const numValue = parseFloat(value);
                     if (!isNaN(numValue)) {
                       setOffsetX(numValue);
                       updateModelInRealTime(numValue, offsetY, rotation);
                     }
                   }
                 }}
                 onBlur={() => {
                   if (offsetXInput === '' || offsetXInput === '-') {
                     setOffsetXInput('0');
                     setOffsetX(0);
                   }
                 }}
                 placeholder="0.00"
               />
            </div>
            <div>
              <label className="v2-ifc-input-panel-label">Norte/Y (m)</label>
                             <input
                 className="v2-ifc-input-panel-input"
                 type="number"
                 step="0.01"
                 value={offsetYInput}
                 onChange={(e) => {
                   const value = e.target.value;
                   setOffsetYInput(value);
                   if (value === '' || value === '-') {
                     setOffsetY(0);
                     updateModelInRealTime(offsetX, 0, rotation);
                   } else {
                     const numValue = parseFloat(value);
                     if (!isNaN(numValue)) {
                       setOffsetY(numValue);
                       updateModelInRealTime(offsetX, numValue, rotation);
                     }
                   }
                 }}
                 onBlur={() => {
                   if (offsetYInput === '' || offsetYInput === '-') {
                     setOffsetYInput('0');
                     setOffsetY(0);
                   }
                 }}
                 placeholder="0.00"
               />
            </div>
          </div>
          <div className="v2-ifc-input-panel-row">
            <div>
              <label className="v2-ifc-input-panel-label">Rotación (°)</label>
                             <input
                 className="v2-ifc-input-panel-input"
                 type="number"
                 step="0.1"
                 value={rotationInput}
                 onChange={(e) => {
                   const value = e.target.value;
                   setRotationInput(value);
                   if (value === '' || value === '-') {
                     setRotation(0);
                     updateModelInRealTime(offsetX, offsetY, 0);
                   } else {
                     const numValue = parseFloat(value);
                     if (!isNaN(numValue)) {
                       setRotation(numValue);
                       updateModelInRealTime(offsetX, offsetY, numValue);
                     }
                   }
                 }}
                 onBlur={() => {
                   if (rotationInput === '' || rotationInput === '-') {
                     setRotationInput('0');
                     setRotation(0);
                   }
                 }}
                 placeholder="0.0"
               />
            </div>
            <div className="v2-ifc-input-panel-button-group">
              <button 
                className="v2-ifc-input-panel-button"
                onClick={handleApplyFineTuning}
              >
                Aplicar
              </button>
              <button 
                className="v2-ifc-input-panel-button"
                onClick={handleResetFineTuning}
              >
                Resetear
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default IfcInputPanel;
